import { useMemo } from "react";

import { useTranslation } from "@/v2/hooks/useTranslation";
import { FreeSpinsSyncResult } from "@/v2/types/FreeSpinsSync/FreeSpinsSyncResult.interface";

import { getFailedSkins } from "../utils/getFailedSkins";
import { getGameFailureCounts } from "../utils/getGameFailureCounts";
import { getUnknownGameCodes } from "../utils/getUnknownGameCodes";

/** The panel text: the counters, then everything that failed. */
export const useFailuresSummary = (result: FreeSpinsSyncResult): string => {
  const { t } = useTranslation();
  const { failures } = result;

  const games = useMemo(() => getGameFailureCounts(failures), [failures]);
  const skins = useMemo(() => getFailedSkins(failures).length, [failures]);
  const unknownCodes = useMemo(() => getUnknownGameCodes(failures).length, [failures]);

  const parts = [
    t("free-spin.fs-retrieval-modal.failures", {
      valuesFound: result.valuesFound,
      valuesPublished: result.valuesPublished,
    }),
    // By currency, as most failures are: a game fails in a currency, not whole.
    games.currencies > 0 &&
      t("free-spin.fs-retrieval-modal.currencies-not-synchronized", {
        currencies: games.currencies,
        games: games.gamesWithCurrencies,
      }),
    games.gamesWithoutCurrency > 0 &&
      t("free-spin.fs-retrieval-modal.games-not-synchronized", {
        count: games.gamesWithoutCurrency,
      }),
    skins > 0 && t("free-spin.fs-retrieval-modal.skins-not-synchronized", { count: skins }),
    unknownCodes > 0 &&
      t("free-spin.fs-retrieval-modal.unknown-game-codes", { count: unknownCodes }),
  ];

  return `${parts.filter(Boolean).join("; ")}.`;
};
